import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, Stethoscope } from "lucide-react";
import { HERO, IMAGES, SITE } from "@/lib/site";

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      aria-labelledby="hero-heading"
      className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-slate-50"
    >
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-sky-100/60 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-24 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-blue-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-blue-800 mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              {HERO.eyebrow}
            </div>

            <h1
              id="hero-heading"
              data-testid="hero-heading"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900"
            >
              Precision Cancer Care with{" "}
              <span className="italic text-blue-800">
                Compassion and Clarity
              </span>
              .
            </h1>

            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
              {HERO.subheading}
            </p>

            <p className="mt-4 text-base text-slate-500 leading-relaxed max-w-2xl italic">
              {HERO.supporting}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/contact"
                data-testid="hero-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue-900 text-white font-semibold px-7 py-4 rounded-full hover:bg-blue-800 transition-colors shadow-lg shadow-blue-900/10"
              >
                Book a Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/expertise"
                data-testid="hero-expertise-btn"
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-900 border border-slate-200 font-semibold px-7 py-4 rounded-full hover:bg-slate-50 transition-colors"
              >
                View Areas of Expertise
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              <Metric value="10+" label="Years experience" />
              <Metric value="8" label="Cancer specialities" />
              <Metric value="10+" label="Advanced techniques" />
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-blue-200/60 via-white to-sky-100/50 blur-2xl" />
              <div className="relative rounded-[2.5rem] overflow-hidden border border-white shadow-2xl shadow-blue-900/10 aspect-[4/5] bg-blue-50">
                <img
                  src={IMAGES.heroDoctor}
                  alt="Dr. Anusha Mandava, Radiation Oncologist in Hyderabad, in consultation"
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>

              <div className="absolute -bottom-6 -left-6 md:-left-10 bg-white rounded-2xl shadow-xl shadow-blue-900/10 border border-slate-100 p-4 md:p-5 max-w-[260px]">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700">
                    <Stethoscope className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">
                      Dr. Anusha Mandava
                    </div>
                    <div className="text-xs text-slate-500">
                      MBBS · Radiation Oncology
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-600">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  Evidence-based · Patient-specific
                </div>
              </div>

              <div className="hidden md:flex absolute -top-4 -right-6 bg-white rounded-2xl shadow-xl shadow-blue-900/10 border border-slate-100 px-4 py-3 items-center gap-3">
                <div className="text-2xl font-serif text-blue-900">10</div>
                <div className="text-xs text-slate-600 leading-tight">
                  Years of
                  <br />
                  clinical experience
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Metric = ({ value, label }) => (
  <div>
    <div className="font-serif text-3xl text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 mt-1 leading-tight">{label}</div>
  </div>
);

export default Hero;
